import { useEffect, useMemo, useState } from 'react'

import { amplitudeTracker } from '@/utils/amplitude'
import { isBrowser } from '@/utils/common'

import type { Identify } from '@amplitude/analytics-types'
import type { ExperimentGroups, ExperimentKeys } from './types/experiment-key.type'

const EXPERIMENTS: ExperimentsMeta = {
  /**
   * 예시
   */
  // ab_20250226_review_CTA: {
  //   experimentGroupKey: 'Web AB Testing Group 202502',
  //   trafficAllocationRule: {
  //     CONTROL: 1,
  //     A: 1,
  //   },
  // },
}

const deleteExpiredExperimentKeys = (storage: ExperimentFlagStorage, whitelistExperimentKeys: string[]) => {
  storage.deleteExpiredExperimentKeys(whitelistExperimentKeys)
}

/**
 * 실험 트래픽 할당 정책을 정의하는 EXPERIMENTS를 사용하여 실험 트래픽을 할당합니다.
 */
const allocateFlagByExperiment = (storage: ExperimentFlagStorage, experiments: ExperimentsMeta) => {
  const allExperimentsMeta = Object.entries(experiments)

  for (const [experimentKey, experimentMeta] of allExperimentsMeta) {
    const determinedFlag = storage.getFlagByExperimentKey(experimentKey)
    if (determinedFlag !== null) {
      console.log('experimentKey', experimentKey, 'flag', determinedFlag)
      amplitudeTracker.track('web.activate_experiment', {
        experimentKey,
        flag: determinedFlag,
      })
      continue
    }

    const random = Math.random()
    let cumulativeProbability = 0

    const totalProbability = Object.values(experimentMeta.trafficAllocationRule).reduce((sum, prob) => sum + prob, 0)

    for (const [flag, probability] of Object.entries(experimentMeta.trafficAllocationRule)) {
      const normalizedProbability = probability / totalProbability
      cumulativeProbability += normalizedProbability

      if (random <= cumulativeProbability) {
        const experimentGroup = experimentMeta.experimentGroupKey || 'Web AB Testing Group'
        storage.setFlagByExperimentKey(experimentGroup, experimentKey, flag as ExperimentFlag)
        break
      }
    }
    console.log('experimentKey', experimentKey, 'flag', determinedFlag)

    amplitudeTracker.track('web.activate_experiment', {
      experimentKey,
      flag: determinedFlag,
    })
  }
}

/**
 * 실험 트래픽 할당 여부를 확인하는 훅 입니다.
 */
export const useExperimentActivation = ({ experimentKey, flag }: { experimentKey: string; flag: ExperimentFlag }) => {
  const storage = useMemo(() => new ExperimentFlagStorage(), [])
  const [determinedFlag, setDeterminedFlag] = useState<string | null>(null)

  useEffect(() => {
    const storedFlag = storage.getFlagByExperimentKey(experimentKey)
    setDeterminedFlag(storedFlag)
  }, [experimentKey, storage, flag])

  if (determinedFlag === null) {
    return { isActivated: flag === 'CONTROL' }
  }

  return {
    isActivated: determinedFlag === flag,
  }
}

/**
 * Control과 Variant 컴포넌트를 정의합니다.
 * useExperimentActivation 훅을 통해 실험 트래픽 할당 여부를 확인합니다.
 */
export type ControlFlag = 'CONTROL'
export type VariantFlag = 'A' | 'B' | 'C' | 'D' | 'E' // | ...
export type ExperimentFlag = ControlFlag | VariantFlag

export type VariantProps = {
  experimentKey: ExperimentKeys
  flag: ExperimentFlag
  children: React.ReactNode
}

export const Variant: React.FC<VariantProps> = ({ experimentKey, flag, children }) => {
  const { isActivated } = useExperimentActivation({ experimentKey, flag })

  return isActivated ? <>{children}</> : null
}

export type ControlProps = {
  experimentKey: ExperimentKeys
  children: React.ReactNode
}
export const Control: React.FC<ControlProps> = ({ experimentKey, children }: ControlProps) => {
  const isServer = typeof window === 'undefined'

  if (isServer) return <>{children}</>

  return (
    <Variant experimentKey={experimentKey} flag="CONTROL">
      {children}
    </Variant>
  )
}

/**
 * 실험 트래픽 할당 내역을 저장하는 클래스 입니다.
 * 실험 트래픽 할당 내역을 저장하고 조회하는 기능을 제공합니다.
 */
class ExperimentFlagStorage {
  private prefix: string
  private isServer: boolean

  constructor(prefix = 'ab_test') {
    this.prefix = prefix
    this.isServer = typeof window === 'undefined'
  }

  private getKey(key?: string): string {
    return `${this.prefix}.${key}`
  }

  getFlagByExperimentKey(experimentKey: string): string | null {
    if (this.isServer) return 'CONTROL'
    const flag = localStorage.getItem(this.getKey(experimentKey))
    return flag
  }
  setFlagByExperimentKey(experimentGroup: string, experimentKey: string, flag: ExperimentFlag) {
    if (this.isServer) return
    localStorage.setItem(this.getKey(experimentKey), flag)

    const identifyEvent = new amplitudeTracker.Identify()
    identifyEvent.append(experimentGroup, `${experimentKey}: ${flag}`)
    amplitudeTracker.identify(identifyEvent as Identify)
  }
  deleteExpiredExperimentKeys(whitelist: string[]) {
    if (this.isServer) return
    const expiredExperimentKeys = Object.keys(localStorage)
      .filter((key) => key.startsWith(this.prefix)) // prefix로 시작하는 키만 필터링
      .filter((key) => !whitelist.includes(key.replace(`${this.prefix}.`, ''))) // prefix 제거 후 whitelist와 비교
    for (const key of expiredExperimentKeys) {
      localStorage.removeItem(key)
    }
  }
}

/*
 * 실험 트래픽 할당 정책을 정의하는 인터페이스 입니다.
 * 실험 키와 각 대조/실험군으로의 트래픽 할당 정책을 정의합니다.
 */
type ExperimentsMeta = {
  [experimentKey in ExperimentKeys]?: ExperimentMeta
}

type ExperimentMeta = {
  experimentGroupKey: ExperimentGroups
  trafficAllocationRule: TrafficAllocationRule
}

type TrafficAllocationRule = {
  [flag in ExperimentFlag]?: number
}

if (isBrowser()) {
  // 브라우저 환경에서만 즉시 실행
  console.log('initializeExperiments')
  const experiments = EXPERIMENTS
  const storage = new ExperimentFlagStorage()
  deleteExpiredExperimentKeys(storage, Object.keys(experiments))
  allocateFlagByExperiment(storage, experiments)
}
